// "Start a service now" (Session AMC): an immediate, one-off maintenance visit today for every
// lift a contract covers, separate from the automatic PPM forward schedule. For a customer
// signing already overdue under a previous provider who needs the first visit the day of
// signing, not months out on the generated cycle.
//
// Deliberately lighter than BackdatedCompletionModal: this SCHEDULES a visit for an engineer to
// carry out (or, if already done, to be recorded through the normal completion flow afterwards),
// it does not record one that already happened, so there is no checklist step here.
import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import type { ScheduleEngineer, StartServiceNowBilling, StartServiceNowResult, StartServiceNowSnapshot } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ButtonLink, buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";

const FIELD = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

export function StartServiceNowModal({
  contractId,
  siteNameHint,
  onClose,
  onCreated,
}: {
  contractId: string;
  siteNameHint: string; // shown while the readiness snapshot loads
  onClose: () => void;
  // Called once at least one visit was created, so the host refreshes its own view.
  onCreated: () => void;
}) {
  const [phase, setPhase] = useState<"loading" | "form" | "saving" | "done" | "blocked">("loading");
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [snapshot, setSnapshot] = useState<StartServiceNowSnapshot | null>(null);
  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);
  const [engineerId, setEngineerId] = useState("");
  const [billing, setBilling] = useState<StartServiceNowBilling>("standard_cycle");
  const [priceGbp, setPriceGbp] = useState("0.00");
  const [priceReason, setPriceReason] = useState("");

  const [result, setResult] = useState<StartServiceNowResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [snap, engineerRows] = await Promise.all([api.contracts.startServiceNowReadiness(contractId), api.scheduling.engineers()]);
        if (cancelled) return;
        if (!snap.eligible) {
          setBlockedMessage(snap.eligibilityMessage ?? "This contract cannot take an immediate visit.");
          setPhase("blocked");
          return;
        }
        if (snap.lifts.length === 0) {
          setBlockedMessage("This contract has no lifts to service.");
          setPhase("blocked");
          return;
        }
        setSnapshot(snap);
        setEngineers(engineerRows);
        if (engineerRows.length === 1) setEngineerId(engineerRows[0]!.id);
        setPhase("form");
      } catch {
        if (cancelled) return;
        setBlockedMessage("Could not load this contract's lifts. Please try again.");
        setPhase("blocked");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contractId]);

  async function save() {
    setError(null);
    let agreedPricePence: number | undefined;
    if (billing === "separate") {
      const pounds = Number(priceGbp);
      if (!Number.isFinite(pounds) || pounds < 0) {
        setError("Enter a valid price, GBP 0.00 or more.");
        return;
      }
      if (!priceReason.trim()) {
        setError("Enter a reason for billing this visit separately.");
        return;
      }
      agreedPricePence = Math.round(pounds * 100);
    }
    setPhase("saving");
    try {
      const res = await api.contracts.startServiceNow(contractId, {
        ...(engineerId ? { engineerId } : {}),
        billing,
        ...(agreedPricePence !== undefined ? { agreedPricePence, agreedPriceReason: priceReason.trim() } : {}),
      });
      setResult(res);
      setPhase("done");
      if (res.created.length > 0) onCreated();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : "Could not create the visit. Please try again.");
      setPhase("form");
    }
  }

  const busy = phase === "saving";
  const notReady = snapshot?.lifts.filter((l) => !l.ready) ?? [];

  return (
    <ModalShell ariaLabel="Start a service now" onClose={() => !busy && onClose()} width="max-w-[560px]" panelClassName="p-6 max-h-[85vh] overflow-y-auto">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-ele-strong">Start a service now</h2>
          <p className="text-sm text-ele-body">{snapshot?.contract.siteName ?? siteNameHint}</p>
        </div>
        <button onClick={() => !busy && onClose()} aria-label="Close" className="text-ele-body hover:text-ele-green">
          ✕
        </button>
      </div>

      {phase === "loading" && <Loader shape="inline" />}

      {phase === "blocked" && (
        <>
          <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {blockedMessage}
          </p>
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}>
              Close
            </button>
          </div>
        </>
      )}

      {phase === "done" && result && (
        <>
          <p className="rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {result.created.length > 0
              ? `${result.created.length} visit${result.created.length === 1 ? "" : "s"} scheduled for today: ${result.created.map((c) => c.reference ?? c.ref).join(", ")}.`
              : "No visit was created."}
          </p>
          {result.skipped.length > 0 && (
            <p role="alert" className="mt-3 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
              {result.skipped.map((s) => `${s.ref}: ${s.reason}`).join(" ")}
            </p>
          )}
          <p className="mt-3 text-sm text-ele-body">
            {result.billing.choice === "separate"
              ? `Billed separately: this visit will raise a Xero DRAFT invoice for GBP ${((result.billing.agreedPricePence ?? 0) / 100).toFixed(2)} ex VAT once approved.`
              : "Covered by the contract's own recurring invoicing; nothing extra will be charged for this visit."}
            {" "}The normal maintenance schedule continues unaffected: the next auto-generated visit still lands on the contract's usual cadence.
          </p>
          <div className={`mt-5 gap-3 sm:items-center ${MODAL_ACTIONS}`}>
            <ButtonLink to="/office/scheduling" variant="secondary" className={MODAL_ACTION_BTN}>
              Open Scheduling to assign an engineer
            </ButtonLink>
            <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}>
              Close
            </button>
          </div>
        </>
      )}

      {(phase === "form" || phase === "saving") && snapshot && (
        <>
          <p className="mb-4 text-sm text-ele-body">
            Creates an immediate visit today for every lift this contract covers, separate from the automatic maintenance
            schedule (which carries on unaffected). Use this when a customer signs already overdue under a previous
            provider and the first visit needs to happen today, not on the generated cycle.
          </p>

          <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Lifts to visit today</h3>
          <ul className="mb-4 space-y-2">
            {snapshot.lifts.map((l) => (
              <li key={l.assetId} className="rounded-ele border border-ele-purple-accent px-3 py-2 text-sm">
                <span className="text-ele-strong">{l.ref}</span>
                {l.ready ? (
                  <span className="ml-2 text-ele-green">SIM and RFID ready</span>
                ) : (
                  <span className="ml-2 text-amber-300">{l.warnings.join(" ")}</span>
                )}
              </li>
            ))}
          </ul>
          {notReady.length > 0 && (
            <p role="alert" className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
              {notReady.length} of {snapshot.lifts.length} lift{snapshot.lifts.length === 1 ? "" : "s"} not fully SIM/RFID ready (above). This does not
              block the visit; the engineer can action it on site.
            </p>
          )}

          <label className="mb-4 block">
            <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Engineer (optional now)</span>
            <select value={engineerId} onChange={(e) => setEngineerId(e.target.value)} className={FIELD}>
              <option value="">Assign later from Scheduling</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>

          <h3 className="mb-2 font-heading text-sm uppercase tracking-wide text-ele-blue">Billing</h3>
          <label className="mb-2 flex cursor-pointer items-start gap-3 rounded-ele border border-ele-purple-accent p-3">
            <input type="radio" name="ssn-billing" checked={billing === "standard_cycle"} onChange={() => setBilling("standard_cycle")} className="mt-1 accent-ele-green" />
            <span className="text-sm text-ele-strong">
              Part of the standard cycle
              <span className="block text-ele-body">Covered by the contract's own recurring invoicing. Nothing extra is charged for this visit.</span>
            </span>
          </label>
          <label className="mb-3 flex cursor-pointer items-start gap-3 rounded-ele border border-ele-purple-accent p-3">
            <input type="radio" name="ssn-billing" checked={billing === "separate"} onChange={() => setBilling("separate")} className="mt-1 accent-ele-blue" />
            <span className="text-sm text-ele-strong">
              Bill separately
              <span className="block text-ele-body">A one-off charge for this visit, e.g. a catch-up under a previous provider. Raises a Xero DRAFT invoice once approved.</span>
            </span>
          </label>

          {billing === "separate" && (
            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Price (GBP ex VAT)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceGbp}
                  onChange={(e) => {
                    setPriceGbp(e.target.value);
                    if (error) setError(null);
                  }}
                  className={FIELD}
                />
              </label>
              <label className="block">
                <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Reason</span>
                <input
                  type="text"
                  value={priceReason}
                  onChange={(e) => {
                    setPriceReason(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="e.g. Catch-up visit, prior provider overdue"
                  className={FIELD}
                />
              </label>
            </div>
          )}

          {error && <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}

          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button onClick={() => !busy && onClose()} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`} disabled={busy}>
              Cancel
            </button>
            <ConfirmButton
              onConfirm={() => void save()}
              label={`Start service now (${snapshot.lifts.length} lift${snapshot.lifts.length === 1 ? "" : "s"})`}
              confirmLabel="Press again to confirm"
              busy={busy}
              busyLabel="Creating..."
              className={buttonClasses("secondary", "md", MODAL_ACTION_BTN)}
            />
          </div>
        </>
      )}
    </ModalShell>
  );
}
